import { useEffect } from "react";
import type { Role } from "../../../shared/types";
import { ApiError } from "../../api";
import { formatDateTime } from "../../../shared/time";

export interface Household {
  id: string;
  name: string;
  notes: string;
}

export interface PageMeta {
  total: number;
  page: number;
  limit: number;
}

const COORDINATORS = new Set<Role>(["owner", "leader", "coordinator"]);

export function canCoordinate(role: Role | undefined): boolean {
  return role !== undefined && COORDINATORS.has(role);
}

export function errorText(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Request failed";
}

export function isDenied(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 403;
}

export function initials(name: string): string {
  const letters = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "");
  return letters.join("") || "?";
}

export function monogramStyle(name: string): { background: string } {
  const hue = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % 360;
  return { background: `linear-gradient(160deg, hsl(${hue} 62% 58%), hsl(${(hue + 40) % 360} 42% 28%))` };
}

export function formatDue(due: string | null): string {
  if (!due) return "No due date";
  const [year, month, day] = due.split("-").map(Number);
  if (!year || !month || !day) return due;
  return new Intl.DateTimeFormat("en-NZ", { day: "numeric", month: "short", year: "numeric" }).format(new Date(year, month - 1, day));
}

export function aucklandToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function safeWhen(iso: string | null | undefined): string {
  if (!iso) return "Time not set";
  if (Number.isNaN(Date.parse(iso))) return iso;
  return formatDateTime(iso);
}

export function relatedHref(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  if (type === "plan") return `/services/${id}`;
  if (type === "event") return `/calendar/${id}`;
  if (type === "group") return `/groups/${id}`;
  if (type === "person") return `/people/${id}`;
  return null;
}

export function usePageTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} · MB Live`;
    return () => {
      document.title = "MB Live";
    };
  }, [title]);
}

export async function apiEnvelope<T>(path: string, init: RequestInit = {}): Promise<{ data: T; meta: PageMeta | null }> {
  const headers = new Headers(init.headers);
  headers.set("X-MB-Client", "mblive");
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(path, { ...init, headers, credentials: "include" });
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) {
    if (!response.ok) throw new ApiError(response.status, "Request failed");
    return { data: undefined as T, meta: null };
  }
  const body = await response.json() as { success: boolean; data: T; error: string | null; meta: PageMeta | null };
  if (response.status === 401 && !path.endsWith("/auth/session")) window.dispatchEvent(new CustomEvent("mb-unauthorized"));
  if (!response.ok || !body.success) throw new ApiError(response.status, body.error ?? "Request failed");
  return { data: body.data, meta: body.meta };
}

export function readMarks(meetingId: string): Record<string, boolean> {
  try {
    const raw = sessionStorage.getItem(`mb-live.attendance.${meetingId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const marks: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") marks[key] = value;
    }
    return marks;
  } catch {
    return {};
  }
}

export function writeMarks(meetingId: string, marks: Record<string, boolean>): void {
  try {
    sessionStorage.setItem(`mb-live.attendance.${meetingId}`, JSON.stringify(marks));
  } catch {
    // Session storage can be blocked. The server still keeps the headcount.
  }
}
